import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'

export const SCROLL_WHEEL_ITEM_H = 44
export const SCROLL_WHEEL_VISIBLE = 5
export const SCROLL_WHEEL_H = SCROLL_WHEEL_ITEM_H * SCROLL_WHEEL_VISIBLE
export const SCROLL_WHEEL_PAD = (SCROLL_WHEEL_H - SCROLL_WHEEL_ITEM_H) / 2

export const SCROLL_WHEEL_COMPACT_ITEM_H = 36
export const SCROLL_WHEEL_COMPACT_VISIBLE = 3
export const SCROLL_WHEEL_COMPACT_H = SCROLL_WHEEL_COMPACT_ITEM_H * SCROLL_WHEEL_COMPACT_VISIBLE
export const SCROLL_WHEEL_COMPACT_PAD = (SCROLL_WHEEL_COMPACT_H - SCROLL_WHEEL_COMPACT_ITEM_H) / 2

const SCROLL_SETTLE_MS = 120

function wheelMetrics(compact: boolean) {
  const itemH = compact ? SCROLL_WHEEL_COMPACT_ITEM_H : SCROLL_WHEEL_ITEM_H
  const wheelH = compact ? SCROLL_WHEEL_COMPACT_H : SCROLL_WHEEL_H
  const pad = compact ? SCROLL_WHEEL_COMPACT_PAD : SCROLL_WHEEL_PAD
  return { itemH, wheelH, pad }
}

interface ScrollSnapWheelProps<T extends string | number> {
  value: T
  onChange: (v: T) => void
  items: readonly T[]
  ariaLabel?: string
  formatItem?: (v: T) => string
  getActiveStyle?: (v: T) => CSSProperties | undefined
  className?: string
  /** Smaller wheel for inline battery row (3 visible rows). */
  compact?: boolean
  /** When false, scroll position updates but no chip is highlighted (e.g. “unknown” year). */
  highlightActive?: boolean
}

/** Vertical scroll wheel with center selection line — one value highlighted in the middle. */
export default function ScrollSnapWheel<T extends string | number>({
  value,
  onChange,
  items,
  ariaLabel = 'Select',
  formatItem = (v) => String(v),
  getActiveStyle,
  className = '',
  compact = false,
  highlightActive = true,
}: ScrollSnapWheelProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout>>()
  const centerRaf = useRef<number>()
  const skipScrollSync = useRef(false)
  const isUserScrolling = useRef(false)
  const { itemH, wheelH, pad } = wheelMetrics(compact)

  const valueIndex = items.indexOf(value)
  const [centerIdx, setCenterIdx] = useState(() => (valueIndex >= 0 ? valueIndex : 0))

  const indexFromScrollTop = useCallback(
    (scrollTop: number) =>
      Math.max(0, Math.min(items.length - 1, Math.round(scrollTop / itemH))),
    [itemH, items.length],
  )

  const scrollToIndex = useCallback((idx: number, smooth = false) => {
    if (!scrollerRef.current) return
    scrollerRef.current.scrollTo({
      top: idx * itemH,
      behavior: smooth ? 'smooth' : 'auto',
    })
  }, [itemH])

  useEffect(() => {
    if (skipScrollSync.current) {
      skipScrollSync.current = false
      return
    }
    isUserScrolling.current = false
    const idx = items.indexOf(value)
    if (idx >= 0) {
      setCenterIdx(idx)
      scrollToIndex(idx)
    }
  }, [value, items, scrollToIndex])

  const pickFromScroll = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const idx = indexFromScrollTop(el.scrollTop)
    setCenterIdx((prev) => (prev === idx ? prev : idx))
    const next = items[idx]
    if (next !== undefined && next !== value) {
      skipScrollSync.current = true
      onChange(next)
    }
  }, [indexFromScrollTop, items, onChange, value])

  const settleScroll = useCallback(() => {
    isUserScrolling.current = false
    pickFromScroll()
  }, [pickFromScroll])

  const scheduleSettle = useCallback(() => {
    clearTimeout(scrollEndTimer.current)
    scrollEndTimer.current = setTimeout(settleScroll, SCROLL_SETTLE_MS)
  }, [settleScroll])

  const handleScroll = () => {
    isUserScrolling.current = true
    const el = scrollerRef.current
    if (el) {
      const idx = indexFromScrollTop(el.scrollTop)
      if (centerRaf.current) cancelAnimationFrame(centerRaf.current)
      centerRaf.current = requestAnimationFrame(() => {
        centerRaf.current = undefined
        setCenterIdx((prev) => (prev === idx ? prev : idx))
      })
    }
    scheduleSettle()
  }

  useEffect(() => {
    const el = scrollerRef.current
    if (!el || typeof el.addEventListener !== 'function') return undefined
    const onScrollEnd = () => {
      clearTimeout(scrollEndTimer.current)
      settleScroll()
    }
    el.addEventListener('scrollend', onScrollEnd)
    return () => {
      el.removeEventListener('scrollend', onScrollEnd)
      clearTimeout(scrollEndTimer.current)
      if (centerRaf.current) cancelAnimationFrame(centerRaf.current)
    }
  }, [settleScroll, items])

  const handlePick = (item: T, idx: number) => {
    skipScrollSync.current = true
    isUserScrolling.current = false
    setCenterIdx(idx)
    onChange(item)
    scrollToIndex(idx, true)
  }

  return (
    <div
      className={`scroll-snap-wheel${compact ? ' scroll-snap-wheel--compact' : ''}${className ? ` ${className}` : ''}`}
      style={{ height: wheelH }}
    >
      <div className="scroll-snap-wheel__fade scroll-snap-wheel__fade--top" aria-hidden />
      <div className="scroll-snap-wheel__fade scroll-snap-wheel__fade--bottom" aria-hidden />
      <div
        ref={scrollerRef}
        className="scroll-snap-wheel__scroller"
        role="listbox"
        aria-label={ariaLabel}
        onScroll={handleScroll}
      >
        <div className="scroll-snap-wheel__pad" style={{ height: pad }} aria-hidden />
        {items.map((item, idx) => {
          const isCenter = idx === centerIdx
          return (
            <button
              key={String(item)}
              type="button"
              role="option"
              aria-selected={item === value}
              className="scroll-snap-wheel__item"
              style={{ height: itemH }}
              onClick={() => handlePick(item, idx)}
            >
              <span
                className={`scroll-snap-wheel__chip${isCenter && highlightActive ? ' scroll-snap-wheel__chip--active' : ''}`}
                style={isCenter && getActiveStyle ? getActiveStyle(item) : undefined}
              >
                {formatItem(item)}
              </span>
            </button>
          )
        })}
        <div className="scroll-snap-wheel__pad" style={{ height: pad }} aria-hidden />
      </div>
    </div>
  )
}
